import { useContext, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { FormProvider, useForm } from 'react-hook-form'
import { Box, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, useMediaQuery } from '@mui/material'

import { AppContext } from '../../../context'
import { QUERY_PARAMS } from '../../../constants'
import { ActionType } from '../../../context/AppContextReducer'
import { anchorOrigin, transformOrigin } from '../../../theme/styleConstants'
import { OrderPaymentBy } from '../../../../generated'
import OfficeAutoComplete from '../../offices/OfficeAutocomplete'
import { AutoCompleteOption } from '../../../interfaceTypes/common'

/**
 * Sort options, in menu order. `groupedToo` marks the ones that also mean something for a day-grouped row —
 * the rest describe a single payment (its cheque number, method, profit centre), which a whole day has none of.
 */
const SORT_OPTIONS: { value: OrderPaymentBy; label: string; groupedToo?: boolean }[] = [
  { value: OrderPaymentBy.Date, label: 'Date', groupedToo: true },
  { value: OrderPaymentBy.Amount, label: 'Amount', groupedToo: true },
  { value: OrderPaymentBy.CheckNo, label: 'Check Number' },
  { value: OrderPaymentBy.Method, label: 'Payment Method' },
  { value: OrderPaymentBy.Location, label: 'Profit Center' }
]

const ReconciliationsSortFilter = () => {
  const methods = useForm()
  const [searchParams, setSearchParams] = useSearchParams()

  const mediumViewport = useMediaQuery('(max-width:1138px)')
  const { dispatch: reducerDispatch } = useContext(AppContext)
  const selectedOffice = searchParams.get(QUERY_PARAMS.OFFICE)
  // Absent param = Line Item Detail ON, matching the rest of the screen.
  const isLineItemView = searchParams.get(QUERY_PARAMS.RECONCILIATION_LINE_ITEMS) !== 'false'
  const sortOptions = isLineItemView ? SORT_OPTIONS : SORT_OPTIONS.filter(option => option.groupedToo)
  // Show what is actually being applied: the grouped view falls back to Date for a sort it cannot honour, and
  // a Select whose value is missing from its options renders blank. The param itself is left alone, so
  // switching Line Item Detail back on restores the sort chosen there.
  const selectedSort = searchParams.get(QUERY_PARAMS.ORDER_BY) || OrderPaymentBy.Date
  const sortValue = sortOptions.some(option => option.value === selectedSort) ? selectedSort : OrderPaymentBy.Date

  // Keep the Profit Center control in step with the URL. The filter can be changed from outside this
  // component — a resumed reconciliation session restores its saved view — and the form field would otherwise
  // keep the previous pick, which is what the box displays. The label is left empty because only the id is in
  // the URL; OfficeAutoComplete resolves the real name from the id once the office list loads.
  useEffect(() => {
    methods.setValue('office', selectedOffice ? { value: Number(selectedOffice), label: '' } : null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOffice])

  const handleSortOrderChange = (event: SelectChangeEvent) => {
    const { value } = event.target
    reducerDispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })
    searchParams.set(QUERY_PARAMS.ORDER_BY, value)
    setSearchParams(searchParams)
  }

  const handleSortDirectionChange = (event: SelectChangeEvent) => {
    const { value } = event.target
    reducerDispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })
    searchParams.set(QUERY_PARAMS.ORDER_DIRECTION, value)
    setSearchParams(searchParams)
  }

  const handleLocationChange = (option: AutoCompleteOption | null) => {
    reducerDispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })
    searchParams.set(QUERY_PARAMS.OFFICE, option?.value?.toString() || '')
    setSearchParams(searchParams)
  }

  return (
    <FormProvider {...methods}>
      <Box display={'flex'} gap={2}>
        <OfficeAutoComplete
          name="office"
          label="Profit Center"
          sx={{ width: '200px' }}
          // The URL (and the session's saved view) carries only the office id, so there is no name to give:
          // passing the id as the name rendered the raw id — "1" — in the box. OfficeAutoComplete fills the
          // real name in by id once the office list loads.
          currentOffice={selectedOffice ? { id: Number(selectedOffice), name: '' } : undefined}
          customOnChange={option => handleLocationChange(option)}
        />

        <FormControl>
          <InputLabel id="sortBy">Sort By:</InputLabel>
          <Select
            labelId="sortBy"
            id="sortBy"
            label={'Sort By:'}
            onChange={handleSortOrderChange}
            displayEmpty={true}
            value={sortValue}
            sx={{ width: mediumViewport ? 262 : 130 }}
            MenuProps={{
              anchorOrigin,
              transformOrigin,
              style: {
                maxHeight: 400
              }
            }}
          >
            {sortOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl>
          <InputLabel id="sortDirection">Sort Direction:</InputLabel>
          <Select
            labelId="sortDirection"
            id="sortDirection"
            label={'Sort Direction:'}
            onChange={handleSortDirectionChange}
            displayEmpty={true}
            value={searchParams.get(QUERY_PARAMS.ORDER_DIRECTION) || 'ASC'}
            sx={{ width: mediumViewport ? 262 : 150 }}
            MenuProps={{
              anchorOrigin,
              transformOrigin,
              style: {
                maxHeight: 400
              }
            }}
          >
            <MenuItem value="ASC">ASC</MenuItem>
            <MenuItem value="DESC">DESC</MenuItem>
          </Select>
        </FormControl>
      </Box>
    </FormProvider>
  )
}

export default ReconciliationsSortFilter
